// Глобален API service: JWT refresh преди logout + коректен login path при subdirectory deploy
import { API_BASE_URL } from '../api';

const LOGIN_PATH = `${(process.env.PUBLIC_URL || '').replace(/\/$/, '')}/login`;

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this._refreshInFlight = null;
  }

  getToken() {
    return localStorage.getItem('token');
  }

  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = LOGIN_PATH || '/login';
  }

  getHeaders(includeContentType = true) {
    const token = this.getToken();
    const headers = {
      Accept: 'application/json',
    };

    if (includeContentType) {
      headers['Content-Type'] = 'application/json';
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Подновява JWT (работи и с изтекъл access token, докато е в JWT_REFRESH_TTL).
   */
  async tryRefreshToken() {
    const token = this.getToken();
    if (!token) return false;
    if (this._refreshInFlight) {
      return this._refreshInFlight;
    }
    this._refreshInFlight = (async () => {
      try {
        const res = await fetch(`${this.baseURL}auth/refresh`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.token) {
          return false;
        }
        localStorage.setItem('token', data.token);
        return true;
      } catch {
        return false;
      } finally {
        this._refreshInFlight = null;
      }
    })();
    return this._refreshInFlight;
  }

  /**
   * При 401 опитва един път refresh и повтаря заявката с новия token.
   */
  async fetchWithAuthRetry(doFetch) {
    let response = await doFetch();
    if (response.status === 401) {
      const ok = await this.tryRefreshToken();
      if (ok) {
        response = await doFetch();
      }
    }
    return response;
  }

  async handleResponse(response) {
    if (response.status === 401) {
      this.logout();
      throw new Error('Session expired');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || `HTTP Error: ${response.status}`);
    }

    return response.json();
  }

  async get(endpoint) {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
    const response = await this.fetchWithAuthRetry(() =>
      fetch(`${this.baseURL}${cleanEndpoint}`, {
        method: 'GET',
        headers: this.getHeaders(false),
      })
    );
    return this.handleResponse(response);
  }

  async post(endpoint, data) {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
    const response = await this.fetchWithAuthRetry(() =>
      fetch(`${this.baseURL}${cleanEndpoint}`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(data),
      })
    );
    return this.handleResponse(response);
  }

  async put(endpoint, data) {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
    const response = await this.fetchWithAuthRetry(() =>
      fetch(`${this.baseURL}${cleanEndpoint}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(data),
      })
    );
    return this.handleResponse(response);
  }

  async delete(endpoint) {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
    const response = await this.fetchWithAuthRetry(() =>
      fetch(`${this.baseURL}${cleanEndpoint}`, {
        method: 'DELETE',
        headers: this.getHeaders(false),
      })
    );
    return this.handleResponse(response);
  }

  async postFormData(endpoint, formData) {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
    const response = await this.fetchWithAuthRetry(() => {
      const token = this.getToken();
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      return fetch(`${this.baseURL}${cleanEndpoint}`, {
        method: 'POST',
        headers,
        body: formData,
      });
    });
    return this.handleResponse(response);
  }

  async patch(endpoint, data) {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
    const response = await this.fetchWithAuthRetry(() =>
      fetch(`${this.baseURL}${cleanEndpoint}`, {
        method: 'PATCH',
        headers: this.getHeaders(),
        body: JSON.stringify(data),
      })
    );
    return this.handleResponse(response);
  }
}

export default new ApiService();
